import { BrowserCacheLocation, InteractionRequiredAuthError, PublicClientApplication, type AccountInfo } from '@azure/msal-browser';
import { customerAppClientId } from '../authConfig';

/**
 * The customer's Global Admin, signed in through the multi-tenant "Customer Connect" app.
 *
 * A second MSAL instance, deliberately separate from the engineer's: different app, different
 * tenant, in-memory cache only (nothing about the customer persists in this browser after the
 * tab closes). Tokens are requested on demand and sent to our API per call; the API never
 * stores them.
 *
 * Two consents happen on first use in a tenant: Graph (sign-in, read principals, grant SPFx
 * API permissions) and then SharePoint (AllSites.FullControl on that tenant's host).
 *
 * Two more are asked for INCREMENTALLY, only at the azure step of a product that ships an Azure
 * pack, never at connect: Azure Resource Manager (user_impersonation — DM deploys the pack as this
 * admin) and, when the pack has an app registration, Graph Application.ReadWrite.All (DM creates the
 * product API's app registration). Refusing either is fine: the step falls back to "Deploy to Azure".
 *
 * Every call names its scopes explicitly, and only the azure step asks for the extra two. Note that
 * Entra puts EVERY scope the admin has consented to for a resource into that resource's access
 * token, whatever the request names — so while the consent stands, any Graph token carries
 * Application.ReadWrite.All. That is why the azure step removes the consent again once the backend
 * is done (AUTH-ARCHITECTURE.md, v2), rather than relying on the request scopes.
 */

export interface CustomerTokens { sharePoint?: string; graph?: string; sharePointHost?: string; arm?: string }

export interface CustomerConnection {
  tenantId: string;
  username: string;
  sharePointHost: string;          // https://{name}.sharepoint.com
}

const GRAPH_SCOPES = [
  'https://graph.microsoft.com/User.Read',
  'https://graph.microsoft.com/Application.Read.All',
  'https://graph.microsoft.com/DelegatedPermissionGrant.ReadWrite.All',
];
const spScopes = (host: string) => [`${host}/AllSites.FullControl`];
const ARM_SCOPES = ['https://management.azure.com/user_impersonation'];
const APP_WRITE_SCOPES = [...GRAPH_SCOPES, 'https://graph.microsoft.com/Application.ReadWrite.All'];

let pca: PublicClientApplication | null = null;
let account: AccountInfo | null = null;
let connection: CustomerConnection | null = null;

async function instance(): Promise<PublicClientApplication> {
  if (!pca) {
    pca = new PublicClientApplication({
      auth: { clientId: customerAppClientId, authority: 'https://login.microsoftonline.com/organizations',
              redirectUri: window.location.origin },
      cache: { cacheLocation: BrowserCacheLocation.MemoryStorage },
    });
    await pca.initialize();
  }
  return pca;
}

/** silentOnly: never open a popup (background polling must not; a popup there would be blocked). */
async function token(scopes: string[], silentOnly = false): Promise<string> {
  const app = await instance();
  if (!account) throw new Error('The customer Global Admin is not signed in.');
  try {
    return (await app.acquireTokenSilent({ scopes, account })).accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError && !silentOnly) return (await app.acquireTokenPopup({ scopes, account })).accessToken;
    throw e;
  }
}

/** Derives the SharePoint host from the tenant's initial onmicrosoft.com domain. */
async function discoverSharePointHost(graphToken: string): Promise<string> {
  const res = await fetch('https://graph.microsoft.com/v1.0/organization?$select=verifiedDomains', {
    headers: { Authorization: `Bearer ${graphToken}` },
  });
  if (!res.ok) throw new Error('Could not read the tenant\'s domains.');
  const org = (await res.json()).value?.[0];
  const initial = org?.verifiedDomains?.find((d: { isInitial: boolean; name: string }) => d.isInitial)?.name as string | undefined;
  if (!initial?.endsWith('.onmicrosoft.com')) throw new Error('Could not determine the tenant\'s SharePoint address.');
  return `https://${initial.replace('.onmicrosoft.com', '').toLowerCase()}.sharepoint.com`;
}

/**
 * Opens the Microsoft sign-in popup for the customer's Global Admin, collects both consents and
 * returns the tenant they signed in to. The API independently refuses a tenant other than the
 * licensed one.
 */
export async function connectCustomer(loginHint?: string): Promise<CustomerConnection> {
  const app = await instance();
  const result = await app.loginPopup({ scopes: GRAPH_SCOPES, prompt: 'select_account', loginHint });
  account = result.account;
  const graph = await token(GRAPH_SCOPES);
  const host = await discoverSharePointHost(graph);
  await token(spScopes(host));                       // second consent, SharePoint
  connection = { tenantId: result.tenantId.toLowerCase(), username: result.account.username, sharePointHost: host };
  return connection;
}

export function currentCustomer(): CustomerConnection | null { return connection; }

export interface TokenOptions {
  /** Include the Azure Resource Manager token (azure step of a product with a pack). */
  azure?: boolean;
  /** Ask Graph for Application.ReadWrite.All too (the pack creates an app registration). */
  appRegistration?: boolean;
  /** Never open a consent popup — for background polling. */
  silentOnly?: boolean;
}

export async function customerTokens(opts: TokenOptions = {}): Promise<CustomerTokens> {
  if (!connection) return {};
  return {
    graph: await token(opts.appRegistration ? APP_WRITE_SCOPES : GRAPH_SCOPES, opts.silentOnly),
    sharePoint: await token(spScopes(connection.sharePointHost), opts.silentOnly),
    sharePointHost: connection.sharePointHost,
    ...(opts.azure ? { arm: await token(ARM_SCOPES, opts.silentOnly) } : {}),
  };
}

/**
 * Asks the signed-in admin for Azure consent (and, for a pack with an app registration, for
 * Application.ReadWrite.All). Throws if they refuse or close the popup — the caller then offers
 * "Deploy to Azure" instead.
 */
export async function connectAzure(appRegistration: boolean): Promise<void> {
  await token(ARM_SCOPES);
  if (appRegistration) await token(APP_WRITE_SCOPES);
}

export async function disconnectCustomer(): Promise<void> {
  if (pca && account) await pca.clearCache({ account });
  account = null;
  connection = null;
}
